import { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { ModalShell, ModalInputField } from "../components/ModalShell";
import { MonthYearPicker } from "../components/MonthYearPicker";
import { FileUpload } from "../components/FileUpload";
import {
  ProposeChangeNotice,
  ReviewLockNotice,
} from "../components/ReviewLockNotice";
import { canRequestChange, reviewedFields } from "../utils/credentialEdit";
import {
  createCredential,
  getCredential,
  updateCredential,
} from "../api/credentials";
import { createDownloadUrl } from "../api/storage";
import { useAuth } from "../hooks/useAuth";
import { reviewLockNotice } from "../utils/credentialLock";
import { formatDobToDmy } from "../utils/date";
import { C } from "../styles/tokens";

// const DEGREE_OPTIONS = [
//   "Diploma",
//   "Certificate",
//   "B.E. / B.Tech",
//   "B.Sc.",
//   "B.A.",
//   "B.Com.",
//   "BBA",
//   "BCA",
//   "M.E. / M.Tech",
//   "M.Sc.",
//   "M.A.",
//   "M.Com.",
//   "MBA",
//   "MCA",
//   "Ph.D.",
//   "Other",
// ];

export function AddEducationPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { localUser } = useAuth();

  const aadhaarName =
    localUser?.aadhaarVerification?.nameOnAadhaar ||
    localUser?.aadhaarVerification?.name ||
    localUser?.fullName ||
    localUser?.professional?.fullName ||
    "";

  const aadhaarDobRaw =
    localUser?.aadhaarVerification?.dob ||
    localUser?.aadhaarVerification?.dateOfBirth ||
    localUser?.dob ||
    localUser?.dateOfBirth ||
    localUser?.professional?.dob ||
    "";
  const aadhaarDob = formatDobToDmy(aadhaarDobRaw);

  const [form, setForm] = useState({
    name: aadhaarName,
    dob: aadhaarDob,
    enrollmentNo: "",
    title: "",
    issuer: "",
    department: "",
    grade: "",
    startDate: "",
    endDate: "",
    isCurrent: false,
    description: "",
    achievements: "",
  });
  const [certificate, setCertificate] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changeable, setChangeable] = useState(false);
  const isVerified = status === "VERIFIED";
  const isView =
    location.pathname.includes("/view/") || searchParams.get("view") === "1";
  const isViewOnly = isView;
  const proposing =
    changeable && status === "VERIFIED" && searchParams.get("change") === "1";
  const locked =
    isViewOnly || (Boolean(reviewLockNotice(status)) && !proposing);

  useEffect(() => {
    if (!isEdit) {
      if (aadhaarName || aadhaarDobRaw) {
        setForm((f) => ({
          ...f,
          name: f.name || aadhaarName,
          dob: f.dob || aadhaarDob,
        }));
      }
      return;
    }
    (async () => {
      try {
        const c = await getCredential(id);
        setForm({
          name: c.metadata?.name ?? c.metadata?.fullName ?? aadhaarName,
          dob: formatDobToDmy(
            c.metadata?.dob ?? c.metadata?.dateOfBirth ?? aadhaarDobRaw,
          ),
          title: c.title ?? "",
          issuer: c.issuer ?? "",
          department: c.metadata?.department ?? c.metadata?.fieldOfStudy ?? "",
          grade: c.metadata?.grade ?? "",
          enrollmentNo:
            c.metadata?.enrollmentNo ??
            c.metadata?.registrationNo ??
            c.metadata?.rollNumber ??
            "",
          startDate: c.startDate ? c.startDate.slice(0, 7) : "",
          endDate: c.endDate ? c.endDate.slice(0, 7) : "",
          isCurrent: c.isCurrent ?? false,
          description: c.description ?? "",
          achievements: c.metadata?.achievements ?? "",
        });
        setStatus(c.status);
        setChangeable(canRequestChange(c));
        if (c.certificateS3Key) {
          setCertificate({
            s3Key: c.certificateS3Key,
            fileName: c.certificateFileName,
          });
        }
      } catch (err) {
        setError(err.message ?? "Failed to load credential");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, aadhaarName, aadhaarDobRaw, aadhaarDob]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function handleClose() {
    navigate("/profile");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (isViewOnly) return;
    if (locked) {
      if (!form.description.trim()) return setError("Add the additional info.");
      return save({ description: form.description.trim() });
    }

    const nameToSubmit =
      form.name.trim() || aadhaarName || "Verified Professional";
    const dobToSubmit = form.dob?.trim() || aadhaarDob || "";

    if (!nameToSubmit) return setError("Enter your name.");
    if (!form.enrollmentNo.trim())
      return setError("Enter the Enrollment No / Registration No.");
    if (!form.title.trim()) return setError("Enter the degree.");
    if (!form.issuer.trim()) return setError("Enter the issuing institution.");
    if (!form.department.trim()) return setError("Enter the department.");
    if (!form.grade.trim()) return setError("Enter the grade or CGPA.");
    if (!form.startDate) return setError("Pick the start month and year.");
    if (!form.isCurrent && !form.endDate) {
      return setError(
        'Pick the end month and year, or tick "I am currently studying here".',
      );
    }
    if (!form.description.trim()) return setError("Add the additional info.");
    const achievementWords = form.achievements?.trim()
      ? form.achievements.trim().split(/\s+/).filter(Boolean).length
      : 0;
    if (achievementWords > 500) {
      return setError(
        `Academic achievements and awards cannot exceed 500 words (currently ${achievementWords} words).`,
      );
    }

    const startDate = `${form.startDate}-01`;
    const endDate =
      form.endDate && !form.isCurrent ? `${form.endDate}-01` : undefined;

    return save({
      type: "EDUCATION",
      title: form.title.trim(),
      issuer: form.issuer.trim(),
      startDate,
      endDate,
      isCurrent: form.isCurrent,
      description: form.description.trim(),
      metadata: {
        name: nameToSubmit,
        fullName: nameToSubmit,
        dob: dobToSubmit,
        dateOfBirth: dobToSubmit,
        department: form.department.trim(),
        fieldOfStudy: form.department.trim(),
        grade: form.grade.trim(),
        enrollmentNo: form.enrollmentNo.trim(),
        registrationNo: form.enrollmentNo.trim(),
        achievements: form.achievements.trim(),
      },
      certificateS3Key: certificate?.s3Key ?? null,
      certificateFileName: certificate?.fileName ?? null,
    });
  }

  /** Adds the credential, or saves the changes to the one being edited. */
  async function save(payload) {
    if (proposing) {
      navigate("/checkout", {
        state: {
          type: "VERIFICATION_FEE",
          credentialId: id,
          editChanges: reviewedFields(payload),
        },
      });
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateCredential(id, payload);
      } else {
        await createCredential(payload);
      }

      handleClose();
    } catch (err) {
      setError(err.message ?? "Failed to save");
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <ModalShell
        title={
          isViewOnly
            ? "View Education"
            : proposing
              ? "Change verified details"
              : isEdit && isVerified
                ? "Edit Additional Info"
                : isEdit
                  ? "Edit Education"
                  : "Add Education"
        }
        subtitle={
          isViewOnly
            ? "Academic qualification & filled details (view-only)"
            : proposing
              ? "Request a change to verified details (verification fee applies)"
              : isEdit && isVerified
                ? "Update additional info (verified details are locked)"
                : "Add your academic qualification details for identity verification"
        }
        onClose={handleClose}
        maxWidth="max-w-xl"
      >
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Loading qualification details...
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 overflow-hidden"
          >
            {/* Form Fields Body */}
            <div className="px-6 py-5 overflow-y-auto space-y-4 flex-1">
              {proposing ? (
                <ProposeChangeNotice onCancel={() => setSearchParams({})} />
              ) : (
                <ReviewLockNotice
                  status={status}
                  onRequestChange={
                    status === "VERIFIED" && changeable
                      ? () => setSearchParams({ change: "1" })
                      : undefined
                  }
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Name"
                  placeholder="Auto-filled from Aadhaar"
                  value={form.name}
                  disabled
                  readOnly
                  tabIndex={-1}
                  onKeyDown={(e) => e.preventDefault()}
                  style={{
                    backgroundColor: "#f8fafc",
                    color: "#1e293b",
                    cursor: "not-allowed",
                  }}
                  hint="Auto-filled from Aadhaar (non-editable)"
                />
                <ModalInputField
                  label="Enrollment No / Registration No"
                  placeholder="e.g. 2021CS101, 111417104001"
                  value={form.enrollmentNo}
                  onChange={(e) => update("enrollmentNo", e.target.value)}
                  disabled={locked}
                  required
                />
              </div>

              <ModalInputField
                type="text"
                label="Date of Birth"
                placeholder="DD/MM/YYYY"
                value={form.dob}
                disabled
                readOnly
                tabIndex={-1}
                onKeyDown={(e) => e.preventDefault()}
                style={{
                  backgroundColor: "#f8fafc",
                  color: "#1e293b",
                  cursor: "not-allowed",
                }}
                hint="Auto-filled from Aadhaar (non-editable)"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Degree / Qualification"
                  placeholder="e.g. B.Tech,B.Sc., PhD."
                  //options={DEGREE_OPTIONS}
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  disabled={locked}
                  required
                />
                <ModalInputField
                  label="Institution / University"
                  placeholder="e.g. Anna University"
                  value={form.issuer}
                  onChange={(e) => update("issuer", e.target.value)}
                  disabled={locked}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ModalInputField
                  label="Department / Field of Study"
                  placeholder="e.g. Computer Science"
                  value={form.department}
                  onChange={(e) => update("department", e.target.value)}
                  disabled={locked}
                  required
                />
                <ModalInputField
                  label="Grade / CGPA"
                  placeholder="e.g. 8.5 CGPA"
                  value={form.grade}
                  onChange={(e) => update("grade", e.target.value)}
                  disabled={locked}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MonthYearPicker
                  label="Start Month & Year"
                  placeholder="Select start month & year"
                  value={form.startDate}
                  onChange={(v) => update("startDate", v)}
                  disabled={locked}
                  required
                />
                <MonthYearPicker
                  label={form.isCurrent ? "End (current)" : "End Month & Year"}
                  placeholder={
                    form.isCurrent
                      ? "Currently studying here"
                      : "Select end month & year"
                  }
                  value={form.endDate}
                  onChange={(v) => update("endDate", v)}
                  disabled={locked || form.isCurrent}
                  required={!form.isCurrent}
                  align="right"
                />
              </div>

              <label
                className={`flex items-center gap-2 text-xs font-medium text-slate-700 select-none ${
                  locked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.isCurrent}
                  onChange={(e) => {
                    update("isCurrent", e.target.checked);
                    if (e.target.checked) update("endDate", "");
                  }}
                  disabled={locked}
                  className="w-4 h-4 rounded border-slate-300 text-[#2563eb] focus:ring-[#2563eb]/20"
                />
                <span>I am currently studying here</span>
              </label>

              <ModalInputField
                label="Additional info"
                placeholder="Describe projects, specialization, achievements..."
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                disabled={isViewOnly}
                required
              />
              <ModalInputField
                label="Academic Achievements & Awards"
                placeholder="List honors, scholarships, rank, awards, published papers, or dean's list recognitions..."
                as="textarea"
                rows={3}
                value={form.achievements}
                onChange={(e) => update("achievements", e.target.value)}
                disabled={locked}
                hint={`${
                  form.achievements?.trim()
                    ? form.achievements.trim().split(/\s+/).filter(Boolean)
                        .length
                    : 0
                } / 500 words (Optional)`}
              />

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                  Degree Certificate / Mark Sheet{" "}
                  <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <FileUpload
                  category="certificate"
                  value={certificate}
                  onUpload={setCertificate}
                  onClear={() => setCertificate(null)}
                  disabled={locked}
                />
                {!locked && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Upload your degree certificate or official mark sheet if
                    available (PDF, PNG, JPG &le; 5 MB).
                  </p>
                )}
                {certificate?.s3Key && (
                  <div className="mt-2.5">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await createDownloadUrl(
                            certificate.s3Key,
                          );
                          const url = res?.downloadUrl || res?.url || res;
                          if (url) {
                            window.open(url, "_blank", "noopener,noreferrer");
                          } else {
                            throw new Error("Unable to retrieve file URL.");
                          }
                        } catch (err) {
                          setError(err.message || "Failed to open document.");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 text-xs font-semibold transition-colors shadow-xs cursor-pointer"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>View Uploaded Document</span>
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-xs font-medium text-red-700">
                  {error}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {isViewOnly ? (
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-3 rounded-b-2xl shrink-0">
                <span
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md border ${
                    isVerified
                      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : "text-slate-700 bg-slate-100 border-slate-200"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${isVerified ? "bg-emerald-600" : "bg-slate-400"}`}
                  />
                  {isVerified ? "Verified • View-only" : "View-only"}
                </span>
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/credentials/edit/education/${id}`)
                    }
                    className="px-4 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs sm:text-sm font-semibold transition-colors shadow-xs cursor-pointer"
                  >
                    {isVerified ? "Edit Info" : "Edit Details"}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs sm:text-sm font-semibold shadow-sm transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/80 flex items-center justify-between gap-3 rounded-b-2xl shrink-0">
                <div>
                  {isEdit && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/credentials/view/education/${id}`)
                      }
                      className="px-3.5 py-2 rounded-lg border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 text-xs sm:text-sm font-semibold transition-colors shadow-xs cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>View details</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 transition-colors shadow-sm cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2.5 rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs sm:text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#2563eb]/30 cursor-pointer"
                  >
                    {submitting
                      ? "Saving..."
                      : proposing
                        ? "Continue to Fee"
                        : isEdit && isVerified
                          ? "Save Additional Info"
                          : isEdit
                            ? "Save changes"
                            : "Add education"}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </ModalShell>
    </AppShell>
  );
}
